class CheckoutTracker {
    constructor() {
        this.init();
    }

    init() {
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => this.attachCheckoutButtonListeners());
        } else {
            this.attachCheckoutButtonListeners();
        }
    }

    async attachCheckoutButtonListeners() {
        document.addEventListener('click', async (event) => {
            if (event.target.matches('button[name="checkout"]') || event.target.closest('button[name="checkout"]')) {
                event.preventDefault();

                try {
                    await Promise.race([
                        this.sendCheckoutLoggingData(),
                        new Promise((_, reject) => setTimeout(() => reject(new Error('Timeout')), 2000))
                    ])
                } catch (error) {
                    console.error('Checkout logging failed or timed out:', error);
                } finally {
                    window.location.href = '/checkout';
                }
            }
        })
    }

    async sendCheckoutLoggingData() {
        const start = new Date();
        const response = await fetch('/apps/backend/api/v1/checkout-logging', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-Requested-With': 'XMLHttpRequest'
            },
            body: JSON.stringify({
                customer: this.getShopifyCustomerData(),
                cart: this.getShopifyCartData(),
                browser: this.getBrowserInfo(),
                sessionInfo: this.getSessionInfo(),
                performanceData: this.getPerformanceData(),
                adBlockerInfo: this.getAdBlockerInfo(),
                shopifyExtensionInfo: this.getShopifyExtensionInfo(),
                timestamp: new Date().toISOString(),
            })
        })

        const end = new Date()
        console.log(`Time required for preparing and sending log request: ${end - start} ms`)

        if (!response.ok) {
            throw new Error(`HTTP ${response?.status}: ${response?.statusText}`)
        }
    }

    getShopifyCustomerData() {
        try {
            const customer = window?.Shopify?.customer;

            return {
                logged_in: (customer && customer.id) ? true : false,
                customer_id: customer?.id,
                email: customer?.email
            }
        } catch (error) {
            return {
                message: 'Error occurred while preparing customer data'
            }
        }
    }

    getShopifyCartData() {
        try {
            const cart = window?.Shopify?.cart;

            if (!cart) {
                return { cart_available: false };
            }

            const cart_items = cart?.items?.map((item) => ({
                id: item?.id || null,
                title: item?.title || null,
                sku: item?.sku || null,
                price: item?.price || null,
                quantity: item?.quantity || null
            }))

            return {
                item_count: cart?.item_count || 0,
                note: cart?.note || null,
                items: cart_items ? cart_items : []
            };
        } catch (error) {
            return {
                message: 'Error occurred while preparing cart data'
            }
        }
    }

    getBrowserInfo() {
        try {
            return {
                user_agent: navigator?.userAgent || null,
                language: navigator?.language || null,
                platform: navigator?.platform || null,
                cookie_enabled: navigator?.cookieEnabled || null,
                screen: {
                    width: screen?.width || null,
                    height: screen?.height || null,
                    color_depth: screen?.colorDepth || null,
                    pixel_depth: screen?.pixelDepth || null,
                    available_width: screen?.availWidth || null,
                    available_height: screen?.availHeight || null
                },
                viewport: {
                    width: window?.innerWidth || null,
                    height: window?.innerHeight || null,
                    scroll_x: window?.scrollX || null,
                    scroll_y: window?.scrollY || null
                },
                device: {
                    memory: navigator?.deviceMemory || 'unknown',
                    hardware_concurrency: navigator?.hardwareConcurrency || 'unknown',
                    connection: navigator?.connection ? {
                        effective_type: navigator?.connection?.effectiveType || null,
                        downlink: navigator?.connection?.downlink || null,
                        rtt: navigator?.connection?.rtt || null
                    } : 'unknown'
                },
                /*capabilities: {
                    local_storage: typeof(Storage) !== "undefined",
                    session_storage: typeof(Storage) !== "undefined",
                    geolocation: 'geolocation' in navigator,
                    touch_support: 'ontouchstart' in window,
                    webgl: !!window.WebGLRenderingContext
                }*/
            }
        } catch (error) {
            return {
                message: 'Error occurred while preparing browser info'
            }
        }
    }

    getSessionInfo() {
        try {
            return {
                url: window?.location?.href || null,
                host: window?.location?.host || null,
                protocol: window?.location?.protocol || null,
                referrer: document?.referrer || null,
                ready_state: document?.readyState || null,
                session_start: performance?.timeOrigin || null,
                page_load_time: performance?.now() || null,
            }
        } catch (error) {
            return {
                message: 'Error occurred while preparing session info'
            }
        }
    }

    getPerformanceData() {
        try {
            return {
                memory: performance.memory ? {
                    used: performance.memory?.usedJSHeapSize || null,
                    total: performance.memory?.totalJSHeapSize || null,
                    limit: performance.memory?.jsHeapSizeLimit || null
                } : 'unavailable',
            }
        } catch(error) {
            return {
                message: 'Error occurred while preparing performance data'
            }
        }
    }

    getAdBlockerInfo() {
        return {
            detected_ad_blocker: this.detectAdBlocker(),
            tracking_protection: this.detectTrackingProtection(),
            script_blocking: this.detectScriptBlocking(),
        }
    }

    detectAdBlocker() {
        try {
            const indicators = {
                ublock_origin: typeof window.uBlockOrigin !== 'undefined',
                adblock_plus: typeof window.adblockplus !== 'undefined',
                ghostery: typeof window.ghostery !== 'undefined',
                privacy_badger: typeof window.privacyBadgerEnabled !== 'undefined'
            };

            return {
                indicators: indicators,
            }
        } catch (error) {
            return {
                message: 'Error occurred while testing for ad-blocking measures'
            }
        }
    }

    detectTrackingProtection() {
        try {
            return {
                do_not_track: navigator.doNotTrack === '1',
                global_privacy_control: navigator.globalPrivacyControl === true,
            };
        } catch (error) {
            return {
                message: 'Error occurred while testing for tracking protection'
            }
        }
    }

    detectScriptBlocking() {
        try {
            return {
                google_analytics: typeof window.gtag !== 'undefined' || typeof window.ga !== 'undefined',
                facebook_pixel: typeof window.fbq !== 'undefined',
                google_tag_manager: typeof window.dataLayer !== 'undefined',
                shopify_analytics: typeof window.ShopifyAnalytics !== 'undefined'
            };
        } catch (error) {
            return {
                message: 'Error occurred while testing for tracking protection'
            }
        }
    }

    getShopifyExtensionInfo() {
        return {
            checkout_extensions: this.detectCheckoutExtensions(),
            shopify_scripts: this.detectShopifyScripts(),
        }
    }

    detectCheckoutExtensions() {
        try {
            const extensions = {
                // Check for common extension containers
                checkout_ui_extensions: document.querySelectorAll('[data-shopify-checkout-ui-extension]').length > 0,
                
                // Check for extension script tags
                extension_scripts: Array.from(document.scripts).filter(script => 
                    script.src && (
                        script.src.includes('checkout-ui-extensions') ||
                        script.src.includes('shopify-checkout') ||
                        script.src.includes('extensions')
                    )
                ).length,
                
                // Check for extension-related DOM elements
                extension_elements: this.findExtensionElements(),
            };

            return extensions;

        } catch (error) {
            return {
                message: 'Error occurred while checking for Shopify checkout extensions'
            } 
        }
    }

    detectShopifyScripts() {
        try {
            const shopifyScripts = Array.from(document.scripts).filter(script => 
                script.src && (
                    script.src.includes('shopify') ||
                    script.src.includes('shopifycdn') ||
                    script.src.includes('myshopify')
                )
            );

            return {
                total_shopify_scripts: shopifyScripts.length,
                script_sources: shopifyScripts.map(script => ({
                    src: script.src,
                    async: script.async,
                    defer: script.defer
                })).slice(0, 10)
            };

        } catch (error) {
            return {
                message: 'Error occurred while checking for Shopify scripts'
            } 
        }
    }

    findExtensionElements() {
        const selectors = [
            '[data-extension-id]',
            '[data-checkout-extension]',
            '.shopify-checkout-extension',
            '[id*="extension"]',
            '[class*="extension"]'
        ];

        const elements = {};
        selectors.forEach(selector => {
            elements[selector] = document.querySelectorAll(selector).length;
        });

        return elements;
    }

}

let checkoutTrackerInstance = null;

document.addEventListener('DOMContentLoaded', () => {
    if (!checkoutTrackerInstance) {
        checkoutTrackerInstance = new CheckoutTracker();
    }
});

window.addEventListener('load', () => {
    if (!checkoutTrackerInstance) {
        checkoutTrackerInstance = new CheckoutTracker();
    }
});